import * as React from 'react';

import { Progress } from '@longline/aqua-ui/controls/Progress';

import { OrganixStatus } from './OrganixStatus';
import { ForgeApi } from '../api/services/ForgeApi';
import { GridExporterJSON } from '../GridExporterJSON';
import { useGlobalState } from '../contexts/state/GlobalStateContext';
import { IArea, modelCodeFor } from '../types/IArea';
import { IOrganixGridPoint } from '../types/IOrganixGridPoint';
import { TertiaryButton } from '@longline/aqua-ui/controls/TertiaryButton';

interface IProps {
  area: IArea;
  data: IOrganixGridPoint[];
  onCancel: () => void;
  onError: (msg: string) => void;
  onComplete: (data: any) => void;
}

const POLL_INTERVAL = 2000; // ms
// How long polls may keep failing before the job is given up on. A single
// 404 or 502 is routine while Forge's load balancer or a task restarts, and
// the job carries on regardless.
const CONTACT_TIMEOUT = 120000; // ms

const OrganixSimulateRemoteForge = (props: IProps) => {
  const globalState = useGlobalState();

  const [progress, setProgress] = React.useState<number>(0);
  const [status, setStatus] = React.useState("initialising");
  const [remainingTime, setRemainingTime] = React.useState<string | null>(null);
  const jobID = React.useRef("");
  const cancelled = React.useRef(false);
  const interval = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const startTime = React.useRef<number>(0);
  const lastContact = React.useRef<number>(0);

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.round(ms / 1000);
    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes < 60) {
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const finish = async (url: string) => {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { "Accept": "application/json" }
      });

      if (!response.ok) {
        props.onError(`Download failed: ${response.status} ${response.statusText}`);
        return;
      }

      const data = await response.json();
      props.onComplete(data);
    } catch (err) {
      props.onError(`Download failed.`);
    }
  }

  const run = async() => {
    if (!props.area.driverset) {
      props.onError("No driverset selected.");
      return;
    }
    const modelCode = modelCodeFor(props.area.simulationKind);
    const input = await GridExporterJSON.run(globalState.project, props.area.driverset, props.area, modelCode, globalState.structures, props.data, globalState.species);

    if (cancelled.current) return;
    const startedJobID = await ForgeApi.start(input, ["motorganix"]);
    // Cancelled while the job was being created: handleCancel had no job ID
    // to cancel yet, so cancel it here.
    if (cancelled.current) {
      if (startedJobID) ForgeApi.cancel(startedJobID);
      return;
    }
    if (!startedJobID) {
      props.onError("Failed to start simulation.");
      return;
    }
    jobID.current = startedJobID;

    startTime.current = Date.now();
    lastContact.current = Date.now();

    const poll = async () => {
      try {
        const res = await ForgeApi.getJob(jobID.current);
        lastContact.current = Date.now();

        console.log("Forge job poll response:", JSON.stringify(res, null, 2));

        if (cancelled.current == true) return;

        // Forge marks the job failed as soon as its simulation fails, with the
        // engine's one-line reason. The job ID traces it in Forge's logs.
        if (res.status === "failed") {
          if (interval.current !== null) clearInterval(interval.current);
          const reason = res.simulations?.find(s => s.status === "failed")?.error;
          props.onError(`The ORGANIX simulation failed on the server${reason ? `: ${reason}` : ""} (job ${jobID.current}).`);
          return;
        }

        const currentProgress = res.progress ?? 0;
        setProgress(currentProgress);
        setStatus("running");

        // Estimate remaining time when we have meaningful progress
        if (currentProgress > 2) {
          const elapsed = Date.now() - startTime.current;
          const estimatedTotal = elapsed / (currentProgress / 100);
          const remaining = estimatedTotal - elapsed;
          setRemainingTime(formatTime(remaining));
        }

        // Stop polling if job is done
        if (res.status === "completed") {
          if (interval.current !== null) clearInterval(interval.current);
          console.log("Forge job complete: ", Date.now() - startTime.current, "ms");
          setStatus("complete");
          setProgress(100);
          setRemainingTime(null);

          const sim = res.simulations?.find(s => s.simulation_type === "motorganix");
          if (sim?.download_url) {
            finish(sim.download_url);
          } else {
            props.onError("No download URL in completed job.");
          }
        }
      } catch (err) {
        if (cancelled.current) return;

        // err is `unknown` under strict mode; the actual shape is an axios-
        // style error with .response.status. Narrow defensively.
        const statusCode = (err as { response?: { status?: number } })?.response?.status;

        if (statusCode === 500) {
          // This means "task not ready yet", so we *keep polling*
          console.warn("Job not ready yet, continuing to poll...");
          return;
        }

        // Any other HTTP error, or a network error, may be transient: keep
        // polling until Forge has been unreachable for CONTACT_TIMEOUT.
        if (Date.now() - lastContact.current > CONTACT_TIMEOUT) {
          if (interval.current !== null) clearInterval(interval.current);
          props.onError(`Lost contact with the simulation server (job ${jobID.current}${statusCode ? `, HTTP ${statusCode}` : ""}).`);
          return;
        }
        console.warn(`Forge poll failed${statusCode ? ` (HTTP ${statusCode})` : ""}, retrying...`);
      }
    };

    // First call immediately:
    poll();

    // Then poll at intervals:
    interval.current = setInterval(poll, POLL_INTERVAL);
  }

  const stop = () => {
    cancelled.current = true;
    if (interval.current !== null) clearInterval(interval.current);
  }

  React.useEffect(() => {
    run();
    return stop;
  }, []);

  const handleCancel = async () => {
    stop();
    // Not awaited: the server-side stop shouldn't hold up closing the dialog.
    if (jobID.current) ForgeApi.cancel(jobID.current);
    props.onCancel();
  }

  return (
    <OrganixStatus>
      <div style={{ marginBottom: 12 }}>
        <div>ORGANIX simulation {status}.</div>
        {remainingTime &&
          <div>Estimated time remaining: {remainingTime}</div>}
      </div>
      <Progress width="300px" thickness={8} numbered value={progress}/>
      <TertiaryButton onClick={handleCancel}>CANCEL</TertiaryButton>
    </OrganixStatus>
  )
}

export { OrganixSimulateRemoteForge }
